import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatState, Message, Provider, MessageVersion, ContentPart } from '../types';
import { createConsentAwareJSONStorage } from '../utils/consentAwareStorage';
import { DEFAULT_CHAT_PROVIDER, DEFAULT_CHAT_MODEL } from '../config/models';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      messages: [],
      currentProvider: DEFAULT_CHAT_PROVIDER as Provider,
      currentModel: DEFAULT_CHAT_MODEL,
      currentSessionId: null, // Текущая сессия чата
      isCurrentSessionSecret: false, // По умолчанию не секретная
      isActiveStreaming: false, // Флаг активного streaming (для отключения поллинга)
      shouldScrollToBottom: false, // Флаг для принудительной прокрутки вниз (при отправке сообщения)
      showScrollToBottomButton: false, // Флаг для показа кнопки скролла вниз
      pendingSessionRename: null as { sessionId: string; newName: string } | null, // Для обновления названия сессии
      pendingNewSession: null as { id: string; name: string } | null, // Для добавления нового чата в сайдбар (создан из composer)
      // Запоминание последней выбранной модели для каждого провайдера
      lastModelByProvider: {
        chatgpt: 'gpt-5.4-2026-03-05',
        gemini: 'gemini-3.1-flash-lite-preview',
        claude: 'claude-haiku-4-5',
        grok: 'grok-4.3',
        deepseek: 'deepseek-v4-flash'
      },
      isNetworkEnabled: true,
      isReasoningEnabled: false,
      reasoningEffort: 'medium',
      reasoningSummary: false, // По умолчанию выключено (экономия токенов)
      maxOutputTokens: 25000, // Рекомендация OpenAI для reasoning моделей
      // Новые опции для composer menu
      isCodeInterpreterEnabled: false,
      isDeepResearchEnabled: false, // Deep Research режим
      deepResearchMode: 'fast' as 'fast' | 'thorough', // fast = o4-mini, thorough = o3
      verbosity: 'normal',
      // Draft message and files
      draftInput: '',
      draftAttachedFiles: [],
      // Pinned assistant
      pinnedAssistantName: null as string | null,
      // Показывать стоимость под сообщениями
      showCost: false,
      // Scroll to specific message (search)
      highlightMessageId: null as string | null,
      // Auto-send: при установке значения Composer автоматически отправляет сообщение
      pendingAutoSend: null as string | null,
      setPendingAutoSend: (text: string | null) => set({ pendingAutoSend: text }),
      // Deep Research clarification step-by-step Q&A
      clarificationQuestions: null as Array<{ question: string; options: string[] }> | null,
      clarificationStep: 0,
      clarificationAnswers: [] as string[],
      clarificationSelectedOption: null as number | null,
      setClarificationQuestions: (questions: Array<{ question: string; options: string[] }> | null) => set({
        clarificationQuestions: questions,
        clarificationStep: 0,
        clarificationAnswers: [],
        clarificationSelectedOption: null,
      }),
      setClarificationSelectedOption: (idx: number | null) => set({ clarificationSelectedOption: idx }),
      advanceClarification: (answer: string) => {
        const state = get();
        const questions = state.clarificationQuestions;
        if (!questions) return;
        const newAnswers = [...state.clarificationAnswers, answer];
        const nextStep = state.clarificationStep + 1;
        if (nextStep >= questions.length) {
          // All done — build text and auto-send
          const text = questions.map((q, i) => `${q.question}\n→ ${newAnswers[i]}`).join('\n\n');
          set({
            clarificationAnswers: newAnswers,
            clarificationQuestions: null,
            clarificationStep: 0,
            clarificationSelectedOption: null,
            pendingAutoSend: text,
          });
        } else {
          set({
            clarificationAnswers: newAnswers,
            clarificationStep: nextStep,
            clarificationSelectedOption: null,
          });
        }
      },
      dismissClarification: () => set({
        clarificationQuestions: null,
        clarificationStep: 0,
        clarificationAnswers: [],
        clarificationSelectedOption: null,
        isDeepResearchEnabled: false,
      }),

      setHighlightMessageId: (id: string | null) => set({ highlightMessageId: id }),

      addMessage: (message: Message) => 
        set((state) => ({ 
          messages: [...state.messages, message],
          // При добавлении сообщения от пользователя - принудительно скроллим вниз
          shouldScrollToBottom: message.role === 'user' ? true : state.shouldScrollToBottom
        })),

      updateMessage: (id: string, updates: Partial<Message>) =>
        set((state) => {
          return {
            messages: state.messages.map(msg => 
              msg.id === id ? { ...msg, ...updates } : msg
            )
          };
        }),

      updateMessageId: (oldId: string, newId: string) =>
        set((state) => ({
          messages: state.messages.map(msg => 
            msg.id === oldId ? { ...msg, id: newId } : msg
          )
        })),

      clearMessages: async () => {
        // Очищаем локально
        set({ messages: [] });
        
        // Очищаем на сервере
        if (!hasAuthData()) return;
        
        try {
          await apiClient.chat.clearChatHistory();
        } catch (error) {
          console.error('❌ Ошибка очистки истории на сервере:', error);
        }
      },

      resetLocalMessages: () => {
        set({ messages: [], currentSessionId: null });
      },

      loadMessagesFromServer: async (sessionId?: string | null) => {
        try {
          // Ждем инициализации авторизации
          const maxAttempts = 10;
          let attempts = 0;
          
          while (!hasAuthData() && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200));
            attempts++;
          }
          
          const currentSessionId = sessionId !== undefined ? sessionId : get().currentSessionId;
          
          if (!hasAuthData()) {
            return;
          }

          // Если нет sessionId, просто не загружаем историю (пустой чат)
          if (!currentSessionId) {
            set({ messages: [], isCurrentSessionSecret: false });
            return;
          }

          // Если есть sessionId, загружаем историю конкретной сессии
          const aroundMsgId = get().highlightMessageId || undefined;
          let data: any;
          try {
            data = await apiClient.chat.getChatSessionHistory(currentSessionId, 100, aroundMsgId);
          } catch (fetchError: any) {
            // Если сессия не найдена или доступ запрещен, очищаем текущую сессию
            if (
              fetchError.response?.status === 404 ||
              fetchError.response?.status === 403 ||
              fetchError.response?.data?.error?.includes('not found') ||
              fetchError.response?.data?.error?.includes('access denied')
            ) {
              set({ currentSessionId: null, messages: [] });
            }
            return;
          }

          if (data.success && data.messages) {
            // Устанавливаем флаг секретности на основе информации о сессии
            // Если currentSessionId установлен для секретного чата, значит пользователь
            // разблокировал его через PIN, и можно загрузить историю
            set({ isCurrentSessionSecret: data.session?.is_secret || false });

            // Преобразуем сообщения из БД в формат приложения
            const loadedMessages: Message[] = data.messages.map((msg: any) => {
              const message: Message = {
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.created_at), // Date object
                // ✅ Загружаем флаг генерации из БД
                isGenerating: msg.is_generating || false,
                // ✅ НОВОЕ: Восстанавливаем streaming_status из БД
                streamingStatus: msg.streaming_status || undefined,
                // ✅ Deep Research: восстанавливаем лог действий из streaming_status.actions
                ...(msg.streaming_status?.actions && {
                  deepResearchStage: (msg.streaming_status.type === 'web_search' ? 'web_search' : 'research') as 'research' | 'web_search',
                  deepResearchActions: msg.streaming_status.actions,
                  deepResearchMode: 'fast' as const,
                }),
                // ✅ Reasoning chunks: восстанавливаем для интерливинга в ChainOfThought
                ...(msg.streaming_status?.reasoning_chunks && Array.isArray(msg.streaming_status.reasoning_chunks) && {
                  reasoningChunks: msg.streaming_status.reasoning_chunks,
                }),
                // ✅ Deep Research clarification: определяем по JSON content с questions
                ...(() => {
                  if (msg.role !== 'assistant' || !msg.content || typeof msg.content !== 'string') return {};
                  try {
                    const json = JSON.parse(msg.content);
                    if (json?.questions && Array.isArray(json.questions) && json.questions.length > 0) {
                      return { deepResearchStage: 'clarification' as const, deepResearchMode: 'fast' as const };
                    }
                  } catch { /* not JSON */ }
                  return {};
                })(),
                // ✅ НОВОЕ: Загружаем partial_content для отображения при перезагрузке
                partialContent: msg.partial_content || null,
                // ✅ Загружаем errorType из БД для отображения стилизации ошибок
                ...(msg.error && { errorType: msg.error }),
                // Стоимость сообщения в наших токенах
                ...(msg.request_metadata?.totalCost && { cost: msg.request_metadata.totalCost }),
                // ✅ Загружаем reasoning_content (Extended Thinking)
                ...(msg.reasoning_content && { reasoningContent: msg.reasoning_content })
              };
              
              // Добавляем attachments если есть
              if (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) {
                const validAttachments = msg.attachments
                  .filter((att: any) => {
                    // Проверяем наличие s3Url
                    if (!att.s3Url || att.s3Url.trim() === '') {
                      return false;
                    }
                    return true;
                  })
                  .map((att: any) => {
                    // Преобразуем типы: input_image -> image, input_file -> document
                    let type = att.type?.replace('input_', '') || 'file';
                    
                    // ВАЖНО: input_file должен стать 'document' для корректного отображения
                    if (type === 'file') {
                      type = 'document';
                    }
                    
                    const attachment = {
                      id: att.id || `att_${Date.now()}_${Math.random().toString(36).substring(7)}`,
                      type: type, // image, video, audio, document
                      url: att.s3Url,
                      name: att.filename || att.name,
                      mimeType: att.mimeType,
                      size: att.size,
                      s3Key: att.s3Key, // Для восстановления контекста изображений в истории
                      source: att.source, // 'code_interpreter' если это файл из CI
                      thumbnailUrl: att.thumbnailUrl || undefined, // превью первого кадра видео
                      thumbnailS3Key: att.thumbnailS3Key || undefined,
                    };

                    return attachment;
                  });
                
                if (validAttachments.length > 0) {
                  message.attachments = validAttachments;
                }
              }
              
              // Добавляем sources если есть (источники веб-поиска)
              if (msg.sources && Array.isArray(msg.sources) && msg.sources.length > 0) {
                message.sources = msg.sources;
              }
              
              // Добавляем searchQueries если есть (поисковые запросы)
              if (msg.search_queries && Array.isArray(msg.search_queries) && msg.search_queries.length > 0) {
                message.searchQueries = msg.search_queries;
              }
              
              // Добавляем versions если есть (история редактирований)
              if (msg.versions && Array.isArray(msg.versions) && msg.versions.length > 0) {
                message.versions = msg.versions.map((ver: any) => {
                  // Нормализуем attachments версий (s3Url → url, filename → name)
                  let normalizedAttachments = ver.attachments;
                  if (ver.attachments && Array.isArray(ver.attachments) && ver.attachments.length > 0) {
                    normalizedAttachments = ver.attachments
                      .filter((att: any) => {
                        const url = att.url || att.s3Url;
                        return url && url.trim() !== '';
                      })
                      .map((att: any) => {
                        let type = att.type?.replace('input_', '') || 'file';
                        if (type === 'file') type = 'document';
                        return {
                          id: att.id || `att_${Date.now()}_${Math.random().toString(36).substring(7)}`,
                          type,
                          url: att.url || att.s3Url,
                          name: att.name || att.filename,
                          mimeType: att.mimeType,
                          size: att.size,
                          s3Key: att.s3Key,
                          source: att.source
                        };
                      });
                  }
                  return {
                    content: ver.content,
                    attachments: normalizedAttachments,
                    timestamp: new Date(ver.timestamp)
                  };
                });
                message.currentVersionIndex = msg.current_version_index ?? msg.versions.length - 1;
                message.editSessionId = msg.edit_session_id; // Загружаем editSessionId из БД
                
                // Устанавливаем content и attachments из активной версии
                const versionIndex = message.currentVersionIndex;
                if (message.versions && versionIndex !== undefined && message.versions[versionIndex]) {
                  const activeVersion = message.versions[versionIndex];
                  message.content = activeVersion.content;
                  message.attachments = activeVersion.attachments;
                }
              }
              
              return message;
            });

            // Фильтруем дубликаты при загрузке
            // Для каждого editSessionId оставляем только сообщения с versions
            // Важно: добавляем ОБА сообщения в паре (user + assistant)
            
            // Шаг 1: Группируем по editSessionId
            const sessionGroups = new Map<string, Message[]>();
            
            for (const msg of loadedMessages) {
              if (!msg.editSessionId) continue;
              
              if (!sessionGroups.has(msg.editSessionId)) {
                sessionGroups.set(msg.editSessionId, []);
              }
              sessionGroups.get(msg.editSessionId)!.push(msg);
            }
            
            // Шаг 2: Для каждой группы выбираем сообщения с versions
            const sessionMessages = new Map<string, { user?: Message; assistant?: Message }>();
            
            for (const [sessionId, messages] of sessionGroups.entries()) {
              const session: { user?: Message; assistant?: Message } = {};
              
              for (const msg of messages) {
                if (msg.role === 'user') {
                  // Берем user сообщение с versions (или первое, если versions нет)
                  if (!session.user || msg.versions) {
                    session.user = msg;
                  }
                } else if (msg.role === 'assistant') {
                  // Берем assistant сообщение с versions (или первое, если versions нет)
                  if (!session.assistant || msg.versions) {
                    session.assistant = msg;
                  }
                }
              }
              
              sessionMessages.set(sessionId, session);
            }
            
            // Шаг 3: Собираем финальный массив, сохраняя порядок
            const filteredMessages: Message[] = [];
            const processedSessionIds = new Set<string>();
            
            for (const msg of loadedMessages) {
              // Обычные сообщения - добавляем
              if (!msg.editSessionId) {
                filteredMessages.push(msg);
                continue;
              }
              
              // Уже обработали эту сессию? Пропускаем
              if (processedSessionIds.has(msg.editSessionId)) {
                continue;
              }
              
              // Добавляем пару user + assistant из сессии
              const session = sessionMessages.get(msg.editSessionId);
              if (session) {
                if (session.user) {
                  filteredMessages.push(session.user);
                }
                if (session.assistant) {
                  filteredMessages.push(session.assistant);
                }
                processedSessionIds.add(msg.editSessionId);
              }
            }

            set({ messages: filteredMessages });
          }
        } catch (error) {
          console.error('❌ Ошибка загрузки истории:', error);
        }
      },

      setProvider: (provider: Provider) => 
        set((state) => {
          // Восстанавливаем последнюю модель для выбранного провайдера
          const lastModel = state.lastModelByProvider?.[provider];
          return { 
            currentProvider: provider,
            currentModel: lastModel || state.currentModel
          };
        }),

      setModel: (model: string) => 
        set((state) => {
          // Запоминаем выбранную модель для текущего провайдера
          const updatedLastModels: Record<Provider, string> = {
            ...state.lastModelByProvider,
            [state.currentProvider]: model
          } as Record<Provider, string>;
          return { 
            currentModel: model,
            lastModelByProvider: updatedLastModels
          };
        }),

      toggleNetwork: () => 
        set((state) => ({ 
          isNetworkEnabled: !state.isNetworkEnabled 
        })),

      toggleReasoning: () => 
        set((state) => ({ 
          isReasoningEnabled: !state.isReasoningEnabled 
        })),

      setReasoningEffort: (effort: 'low' | 'medium' | 'high' | 'xhigh') =>
        set({ reasoningEffort: effort }),

      setReasoningSummary: (enabled: boolean) => 
        set({ reasoningSummary: enabled }),

      toggleCodeInterpreter: () => 
        set((state) => ({ 
          isCodeInterpreterEnabled: !state.isCodeInterpreterEnabled 
        })),

      toggleDeepResearch: () =>
        set((state) => {
          const newState = !state.isDeepResearchEnabled;
          return {
            isDeepResearchEnabled: newState,
            isNetworkEnabled: newState ? true : state.isNetworkEnabled
          };
        }),

      setDeepResearchMode: (mode: 'fast' | 'thorough') =>
        set({ deepResearchMode: mode }),

      setVerbosity: (verbosity: 'concise' | 'normal' | 'detailed') => 
        set({ verbosity }),

      setMaxOutputTokens: (tokens: number) => 
        set({ maxOutputTokens: tokens }),

      // Session management
      setCurrentSessionId: (sessionId: string | null) =>
        set({ currentSessionId: sessionId }),
      
      setIsCurrentSessionSecret: (isSecret: boolean) =>
        set({ isCurrentSessionSecret: isSecret }),

      // Auto-rename session notification
      setPendingSessionRename: (data: { sessionId: string; newName: string } | null) =>
        set({ pendingSessionRename: data }),

      // New session created from composer (for sidebar update)
      setPendingNewSession: (data: { id: string; name: string } | null) =>
        set({ pendingNewSession: data }),

      // Scroll control
      setShouldScrollToBottom: (value: boolean) =>
        set({ shouldScrollToBottom: value }),
      setShowScrollToBottomButton: (value: boolean) =>
        set({ showScrollToBottomButton: value }),

      // Draft management
      setDraftInput: (input: string) => {
        // Убрали лог - срабатывает при каждом символе
        set({ draftInput: input });
      },

      setDraftAttachedFiles: (files) =>
        set((state) => ({
          draftAttachedFiles: typeof files === 'function' ? files(state.draftAttachedFiles) : files
        })),

      setPinnedAssistantName: (name: string | null) => set({ pinnedAssistantName: name }),

      setShowCost: (value: boolean) => set({ showCost: value }),

      clearDraft: async () => {
        set({ draftInput: '', draftAttachedFiles: [] });
        
        // Очищаем файлы из БД для синхронизации между устройствами
        try {
          const currentSessionId = get().currentSessionId;
          const { apiClient } = await import('../services/api');
          await apiClient.clearChatComposerAttachments(currentSessionId);
        } catch (error) {
          // Не прерываем выполнение - локальное состояние уже очищено
        }
      },

      // Message editing
      editMessage: async (messageId: string, newContent: string | ContentPart[], newAttachments?: any[]) => {
        // Сначала обновляем локальный state
        set((state) => ({
          messages: state.messages.map(msg => {
            if (msg.id !== messageId) return msg;
            
            // Создаем новую версию сообщения
            const newVersion: MessageVersion = {
              content: newContent,
              attachments: newAttachments,
              timestamp: new Date()
            };
            
            let updatedMessage;
            
            // Если versions еще нет, создаем массив со старой версией
            if (!msg.versions) {
              const oldVersion: MessageVersion = {
                content: msg.content,
                attachments: msg.attachments,
                timestamp: msg.timestamp
              };
              
              // Используем существующий editSessionId или создаем новый
              const editSessionId = msg.editSessionId || `edit_${Date.now()}_${Math.random().toString(36).substring(7)}`;
              
              updatedMessage = {
                ...msg,
                content: newContent,
                attachments: newAttachments,
                versions: [oldVersion, newVersion],
                currentVersionIndex: 1, // Новая версия активна
                editSessionId // Привязываем ID сессии редактирования
              };
              
            } else {
              // Если versions уже есть, добавляем новую версию
              updatedMessage = {
                ...msg,
                content: newContent,
                attachments: newAttachments,
                versions: [...msg.versions, newVersion],
                currentVersionIndex: msg.versions.length // Индекс новой версии
                // editSessionId остаётся прежним
              };
              
            }
            
            return updatedMessage;
          })
        }));
        
        
        // ПОСЛЕ обновления state, сохраняем в БД и ждём завершения
        try {
          const state = get();
          const msg = state.messages.find(m => m.id === messageId);
          if (!msg || !hasAuthData()) return;
          
          // Преобразуем versions в сериализуемый формат (Date -> ISO string)
          const serializableVersions = msg.versions?.map(ver => ({
            content: ver.content,
            attachments: ver.attachments,
            timestamp: ver.timestamp instanceof Date ? ver.timestamp.toISOString() : ver.timestamp
          }));
          
          await apiClient.chat.updateVersions({
            messageId: messageId,
            versions: serializableVersions,
            currentVersionIndex: msg.currentVersionIndex,
            editSessionId: msg.editSessionId
          } as any);
        } catch (error) {
          console.error('❌ Error saving message versions:', error);
          throw error; // Пробрасываем ошибку для обработки выше
        }
      },

      switchMessageVersion: (messageId: string, direction: 'prev' | 'next') =>
        set((state) => ({
          messages: state.messages.map(msg => {
            if (msg.id !== messageId || !msg.versions) return msg;
            
            const currentIndex = msg.currentVersionIndex ?? 0;
            let newIndex = currentIndex;
            
            if (direction === 'prev' && currentIndex > 0) {
              newIndex = currentIndex - 1;
            } else if (direction === 'next' && currentIndex < msg.versions.length - 1) {
              newIndex = currentIndex + 1;
            }
            
            // Если индекс не изменился, ничего не делаем
            if (newIndex === currentIndex) return msg;
            
            // Обновляем content и attachments из выбранной версии
            const selectedVersion = msg.versions[newIndex];
            const updatedMessage = {
              ...msg,
              content: selectedVersion.content,
              attachments: selectedVersion.attachments,
              currentVersionIndex: newIndex
            };
            
            // Сохраняем текущий индекс версии в БД асинхронно
            (async () => {
              try {
                if (!hasAuthData()) return;
                
                // Преобразуем versions в сериализуемый формат (Date -> ISO string)
                const serializableVersions = msg.versions?.map(ver => ({
                  content: ver.content,
                  attachments: ver.attachments,
                  timestamp: ver.timestamp instanceof Date ? ver.timestamp.toISOString() : ver.timestamp
                }));
                
                await apiClient.chat.updateVersions({
                  messageId: messageId,
                  versions: serializableVersions,
                  currentVersionIndex: newIndex,
                  editSessionId: msg.editSessionId
                } as any);
              } catch (error) {
                console.error('❌ Error updating version index:', error);
              }
            })();
            
            return updatedMessage;
          })
        })),

      // Удаление сообщения (из локального state и из БД)
      deleteMessage: async (messageId: string) => {
        // Сначала удаляем локально
        set((state) => ({
          messages: state.messages.filter(msg => msg.id !== messageId)
        }));
        
        // Затем удаляем на сервере
        if (!hasAuthData()) return;
          
        try {
          await apiClient.chat.deleteMessage(messageId);
        } catch (error) {
          console.error('❌ Error deleting message:', error);
        }
      },

      // Удаление сообщения только локально (без запроса на сервер)
      removeMessageLocally: (messageId: string) => {
        set((state) => ({
          messages: state.messages.filter(msg => msg.id !== messageId)
        }));
      },
      
      // Удаление сообщения и всех последующих (для удаления сообщения пользователя)
      deleteMessagesFrom: async (messageId: string) => {
        const state = get();
        const messageIndex = state.messages.findIndex(msg => msg.id === messageId);
        
        if (messageIndex === -1) return;
        
        // Получаем ID всех сообщений для удаления
        const messagesToDelete = state.messages.slice(messageIndex);
        const messageIds = messagesToDelete.map(msg => msg.id);
        
        // Сначала удаляем локально
        set((state) => ({
          messages: state.messages.slice(0, messageIndex)
        }));
        
        // Затем удаляем на сервере каждое сообщение
        if (!hasAuthData()) return;
        
        try {
          // Удаляем все сообщения параллельно
          await Promise.all(messageIds.map(id =>
            apiClient.chat.deleteMessage(id)
          ));
        } catch (error) {
          console.error('❌ Error deleting messages:', error);
        }
      },
    }),
    {
      name: 'chat-storage',
      storage: createConsentAwareJSONStorage(), // .ru: гейтится functional consent (FZ-152)
      version: 1,
      migrate: (persistedState: unknown, _version: number) => {
        const state = (persistedState as Record<string, unknown>) ?? {};
        // v1: thinking mode moved to chat-settings drilldown and made opt-in;
        // reset everyone's persisted toggle to off so it defaults off until they enable it themselves.
        return { ...state, isReasoningEnabled: false } as never;
      },
      partialize: (state) => ({
        // messages НЕ сохраняем - будут только после S3 интеграции
        currentProvider: state.currentProvider,
        currentModel: state.currentModel,
        currentSessionId: state.isCurrentSessionSecret ? null : state.currentSessionId,
        lastModelByProvider: state.lastModelByProvider,
        // Persist user's per-message toggles so their override survives reloads.
        // (Defaults are ON for new users, set above in the initial state.)
        isNetworkEnabled: state.isNetworkEnabled,
        isReasoningEnabled: state.isReasoningEnabled,
        reasoningEffort: state.reasoningEffort,
        reasoningSummary: state.reasoningSummary,
        maxOutputTokens: state.maxOutputTokens,
        // ✅ Сохраняем isActiveStreaming для корректной работы при переключении вкладок
        isActiveStreaming: state.isActiveStreaming,
        // Сохраняем только draftInput, но НЕ draftAttachedFiles (они в БД для синхронизации)
        draftInput: state.isCurrentSessionSecret ? '' : state.draftInput,
        // НЕ сохраняем draftAttachedFiles в localStorage - используем только БД
        // Закреплённый ассистент
        pinnedAssistantName: state.pinnedAssistantName,
        showCost: state.showCost,
      }),
    }
  )
);

export { useChatStore };

